/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    BlacklistValidatorFactory,
    DuplicateValueValidatorFactory,
    ExtensionValidatorFactory,
    FormFieldValue,
    LengthValidatorFactory,
    NumberRangeValidatorFactory,
    RequiredValidatorFactory
} from 'src/core/types/form';

/**
 * A factory to create a required validator. A required validator is simply
 * one that succeeds only if the value given is non empty or whitespace.
 */
export const createRequiredValidator: RequiredValidatorFactory = () => (_value: number) => true;

/**
 * A factory to create an extension validator. An extension validator will succeed
 * when the given value has the required extension.
 *
 * @param extension A string of the extension required. Ex: 'txt', 'json', etc. The
 * extension should not be prefixed with a '.', only the string part of the extension
 * should be passed.
 */
export const createExtensionValidator: ExtensionValidatorFactory = (_extension: string) => (_value: number) => false;

/**
 * Creates a validator that validates whether the given value is a duplicate
 * of the original value or not.
 *
 * @param original The original value that is being tested against.
 * @returns A form validator that validates against the original value.
 */
export const createDuplicateValueValidator: DuplicateValueValidatorFactory = (original: number) => (value: number) => original !== value;

/**
 * A factory for creating blacklist validators. A black list validator is one
 * that succeeds validation only if the value given does not contain any of the
 * characters defined in the blacklist.
 *
 * @param blacklist A string of the characters to blacklist. The characters should
 * be contiguous with no white spaces between them. The value to validate should not
 * contain any characters in this string.
 */
export const createBlacklistValidator: BlacklistValidatorFactory = () => (_value: FormFieldValue) => true;

/**
 * A factory for creating range validators. A range validator is one
 * that succeeds validation only if the value given is contained within the
 * values specified.
 *
 * @param range Array of numbers. The array should include only two values representing the valid range for the number.
 */
export const createRangeValidator: NumberRangeValidatorFactory = ({ range, maxMode = 'include', minMode = 'include' }) => (
    value: FormFieldValue
) =>
    (value > range[0] || (minMode === 'include' && value === range[0])) &&
    (value < range[1] || (maxMode === 'include' && value === range[1]));

/**
 * A factory for creating length validators. A length validator is one
 * that succeeds validation only if the length of the value given is is less than the
 * value specified.
 *
 * @param length number. Max char length allowed.
 */
export const createLengthValidator: LengthValidatorFactory = (length: number) => (value: FormFieldValue) =>
    value.toString().length <= length;
